//-----------------------------------------------------------------------------
// Copyright 2021-2025 by banbutsu dcp GmbH. Confidential. All rights reserved.
//-----------------------------------------------------------------------------
// Project: platform frontend
// Author:  thomas.driemeyer@banbutsu.com
//
// handle translation to languages
//
// translate                    translate a constant string to a language
// translateCost                convert a cost to a string, 0 is "free"
// translateCountryCode         convert a cost to a string, 0 is "free"
// translateDate                convert a date to a string, eg. month names
// getMapByLanguage             pick language from a product title or text map
//-----------------------------------------------------------------------------

import { translationsDE } from "./translations/de";
import { translationsEN } from "./translations/en";
import { translationsFR } from "./translations/fr";
import { getCountries } from "react-phone-number-input";
import { registerLocale } from "react-datepicker";
import de from "date-fns/locale/de";
import fr from "date-fns/locale/fr";
import en from "date-fns/locale/en-US";

//
// for all translations in use, register locales for the date picker.
// Update this when adding new translations.
//
registerLocale("de", de);
registerLocale("fr", fr);
registerLocale("en", en);

export const languages = ["en", "de", "fr"];

const translations = new Map<string, Map<string, string>>([
    ["de", translationsDE],
    ["en", translationsEN],
    ["fr", translationsFR],
]);


//
// given a string code and a language, look up the code in the correct language
// map and return the translation in the correct language. The code is usually,
// but not necessarily, the English text; this makes the code more readable.
//
export function translate(
    language: string,
    code:     string
): string {

    if (!translations.has(language)) {
        console.log("unknown language '" + language + "', using 'en'");
        language = "en";
    }
    const t = translations.get(language);

    if (t && t.has(code)) {
        const ret = t.get(code)
        return ret ? ret : "";
    }
    if (translationsEN.has(code)) {
        console.log("no translation for '" + code + "' to '" + language + "', using 'en'");
        const ret = translationsEN.get(code);
        return ret ? ret : "";
    }
    console.log("no translation for '" + code + "'");
    return "";
}


//
// format cost, while translating "free" if the cost is 0. Currently, always
// uses German number formats; changing this would require changing it in many
// other places including the backend, and inconsistency is bad.
//
export const translateCost = (language: string, cost: number) => {

    if (cost === 0) {
        return translate(language, "Free");
    }
    return Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
    }).format(cost / 100);
}


//
// convert a country code string like "de" to a country code enum required by
// the react-phone-number-input component. This component draws the little flag
// and country selector for the phone number field on the confirmation page.
//
const countries = getCountries();
type CountryCode = typeof countries[number];

export const translateCountryCode = (language: string): CountryCode | undefined => {

    if (!language)
        return "DE";

    language = language.toUpperCase();
    return getCountries().includes(language as CountryCode)
           ? (language as CountryCode)
           : 'DE';
}


//
// convert a date to either short format (without weekday) or long format (with
// weekday). It handles mm/dd vs. dd.mm.
//
export const translateDate = (language: string, date: Date, format: string): string => {

    if (!language)
        language = "de"

    let formatter: Intl.DateTimeFormat;
    switch (format) {
      case "wmdy":
            formatter = new Intl.DateTimeFormat(language, {
                day:     '2-digit',
                month:   'numeric',
                year:    'numeric',
                weekday: 'short',
            });
            break;

      default:
      case "wmd":
            formatter = new Intl.DateTimeFormat(language, {
                day:     '2-digit',
                month:   'numeric',
                weekday: 'short',
            });
            break;

      case "md":
            formatter = new Intl.DateTimeFormat(language, {
                day:     '2-digit',
                month:   'numeric',
            });
            break;
    }
    if (typeof(date) === "string") {
      return formatter.format(Date.parse(date));
    }
    return formatter.format(date);
};


//
// the backend often sends text and titles as maps, indexed by a language such
// as "en" or "de". Pick the right map member by language. If it doesn't exist
// pick English.
//
export function getMapByLanguage(
    stringmap: { [key: string]: string } | null | undefined,
    language: string | undefined
): string {

    if (stringmap && language && stringmap[language]) {
        return stringmap[language];
    }
    if (stringmap && stringmap['en']) {
        return stringmap['en'];
    }
    return '';
}
